import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Backdrop,
  CircularProgress,
  Alert,
  Box,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import FolderZipOutlinedIcon from "@mui/icons-material/FolderZipOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { useDispatch } from "react-redux";
import { fetchReport } from "../../../features/slices/Report";
import { AppDispatch } from "../../../store";
import { useNotification } from "../../../context/NotificationContext";
import { useLoader } from "../../../context/LoaderContext";

interface ReportDownloadDialogProps {
  suiteId: number;
  open: boolean;
  onClose: () => void;
}

const ReportDownloadDialog: React.FC<ReportDownloadDialogProps> = ({
  suiteId,
  open,
  onClose,
}) => {
  const [selectedReportType, setSelectedReportType] = useState<string>("all");
  const dispatch = useDispatch<AppDispatch>();
  const { notify } = useNotification();
  const { showLoader, hideLoader } = useLoader();

  const handleDownload = async () => {
    if (!selectedReportType) {
      notify("Please select a report type.", "warning");
      return;
    }
    try {
      showLoader();

      const response = await dispatch(
        fetchReport({ suiteId, reportType: selectedReportType })
      ).unwrap();

      let blob;

      if (selectedReportType === "all") {
        if (typeof response.data === "string") {
          const filePath = response.data;
          window.location.href = `/download?file=${encodeURIComponent(
            filePath
          )}`;
          notify("Report download initiated.", "success");
          hideLoader();
          onClose();
          return;
        } else {
          blob = new Blob([new Uint8Array(response.data)], {
            type: "application/zip",
          });
        }
      } else if (selectedReportType === "byRunResults") {
        blob = new Blob([response.data], { type: "text/html" });
      }

      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download =
        selectedReportType === "all"
          ? `suite_${suiteId}_result.zip`
          : `suite_${suiteId}_report.html`;
      link.click();

      notify("Report downloaded successfully!", "success");
      hideLoader();
      onClose();
    } catch (error) {
      hideLoader();
      console.error("Error downloading the report:", error);
      notify("Failed to download the report. Please try again.", "error");
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 1302 }}
      >
        <DialogTitle>Download report</DialogTitle>
        <DialogContent dividers>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            Choose what to download. The file is prepared on request and saved
            straight to your browser's downloads.
          </Typography>
          <RadioGroup
            value={selectedReportType}
            onChange={(e) => setSelectedReportType(e.target.value)}
          >
            {[
              {
                value: "all",
                icon: <FolderZipOutlinedIcon color="primary" />,
                title: "Full bundle (.zip)",
                desc: "Everything: raw results, logs and artifacts for every case.",
              },
              {
                value: "byRunResults",
                icon: <DescriptionOutlinedIcon color="primary" />,
                title: "Results summary (.html)",
                desc: "A single self-contained HTML page you can open in any browser.",
              },
            ].map((opt) => {
              const selected = selectedReportType === opt.value;
              return (
                <Box
                  key={opt.value}
                  onClick={() => setSelectedReportType(opt.value)}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    p: 1.5,
                    mb: 1,
                    border: "1px solid",
                    borderColor: selected ? "primary.main" : "divider",
                    borderRadius: 2,
                    cursor: "pointer",
                    bgcolor: selected ? "primary.lighter" : "transparent",
                    transition: "all 120ms ease",
                    "&:hover": { borderColor: "primary.light" },
                  }}
                >
                  <FormControlLabel
                    value={opt.value}
                    control={<Radio />}
                    label=""
                    sx={{ m: 0 }}
                  />
                  {opt.icon}
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>
                      {opt.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {opt.desc}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </RadioGroup>
          <Alert severity="info" sx={{ mt: 1 }}>
            Need the rich Allure report instead? Use the{" "}
            <strong>General</strong> button on the suite row — it's built in the
            background and the link arrives in your <strong>Notifications</strong>.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDownload}
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReportDownloadDialog;
